import React, { useState, useRef } from 'react';

/**
 * Modal para resolver o descartar un reporte de documento
 *
 * Requiere evidencia obligatoria (imagen JPG/PNG) para cerrar el reporte.
 *
 * @param {Object} props - Propiedades del componente
 * @param {boolean} props.isOpen - Control de visibilidad del modal
 * @param {Function} props.onClose - Callback para cerrar el modal
 * @param {Function} props.onResolve - Callback para resolver (recibe {evidencia: File, notas: string})
 * @param {Function} props.onDiscard - Callback para descartar (recibe {evidencia: File, notas: string})
 * @param {Object} props.reporte - Reporte a resolver/descartar
 * @param {boolean} props.loading - Estado de carga durante envío
 * @returns {JSX.Element|null}
 */
function ResolverReporteModal({ isOpen, onClose, onResolve, onDiscard, reporte, loading }) {
  const [notas, setNotas] = useState('');
  const [evidencia, setEvidencia] = useState(null);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const ACCEPTED_TYPES = ['image/jpeg', 'image/png'];
  const MAX_SIZE = 5 * 1024 * 1024; // 5MB

  /**
   * Valida y establece el archivo de evidencia seleccionado
   * @param {React.ChangeEvent<HTMLInputElement>} e - Evento de cambio del input file
   */
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setError('');

    if (!file) {
      setEvidencia(null);
      return;
    }

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError('Solo se permiten imágenes JPG y PNG');
      setEvidencia(null);
      return;
    }

    if (file.size > MAX_SIZE) {
      setError('El archivo no debe superar 5MB');
      setEvidencia(null);
      return;
    }

    setEvidencia(file);
  };

  /**
   * Envía el formulario con la acción indicada (resolver o descartar)
   * @param {'resolve'|'discard'} action - Acción a ejecutar
   */
  const handleSubmit = (action) => {
    if (!evidencia) {
      setError('La evidencia es obligatoria');
      return;
    }

    if (action === 'resolve') {
      onResolve({ evidencia, notas });
    } else {
      onDiscard({ evidencia, notas });
    }
  };

  /**
   * Resetea el estado del formulario y cierra el modal
   */
  const handleClose = () => {
    setNotas('');
    setEvidencia(null);
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClose();
  };

  if (!isOpen || !reporte) return null;

  return (
    <div className="modal-backdrop" onClick={handleClose}>
      <div className="modal-container" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Cerrar Reporte</h2>
          <button className="modal-close-btn" onClick={handleClose} aria-label="Cerrar">
            ✕
          </button>
        </div>

        <div className="modal-body">
          <div className="margin-bottom-md">
            <p><strong>Equipo:</strong> {reporte.equipo_codigo}</p>
            <p><strong>Tipo:</strong> {reporte.tipo_documento_nombre}</p>
            <p><strong>Problema:</strong> {reporte.tipo_problema?.replace('_', ' ')}</p>
            <p className="text-muted"><small>{reporte.descripcion}</small></p>
          </div>

          <div className="form-group">
            <label className="form-label">Evidencia de cierre * (JPG, PNG - máx 5MB)</label>
            <input
              type="file"
              ref={fileInputRef}
              className={`form-control ${error ? 'error' : ''}`}
              accept=".jpg,.jpeg,.png"
              onChange={handleFileChange}
            />
            {evidencia && (
              <small className="text-muted">
                Archivo seleccionado: {evidencia.name}
              </small>
            )}
            {error && <div className="text-danger margin-top-xs">{error}</div>}
          </div>

          <div className="form-group">
            <label className="form-label">Notas de cierre</label>
            <textarea
              className="form-textarea"
              rows={3}
              value={notas}
              onChange={e => setNotas(e.target.value)}
              placeholder="Descripción de las acciones tomadas..."
            />
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={handleClose} disabled={loading}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => handleSubmit('discard')}
            disabled={loading}
          >
            {loading ? 'Procesando...' : 'Descartar'}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => handleSubmit('resolve')}
            disabled={loading}
          >
            {loading ? 'Procesando...' : 'Resolver'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ResolverReporteModal;
